/**
 * The calendar a family subscribes to, and the links that hand it over (D-533).
 *
 * **The URL is the whole of the authentication.** A calendar client cannot sign
 * in — no cookie, no OAuth, no bearer header — so an unguessable address is the
 * entire mechanism, which is how every ICS subscription in existence works. It
 * follows that these strings are secrets: copied, never rendered, and never put
 * anywhere a screenshot or a support call would carry them.
 */

/** One subscribable link, as the panel shows it. */
export type CalendarLink = {
  /** A dancer's first name, or "My teaching". */
  name: string

  /** Null for the account's own teaching days. */
  dancerId: string | null

  /** The whole URL, for the copy button. Never rendered. */
  url: string

  /**
   * The same address under `webcal:`, which subscribes in one tap (D-561).
   *
   * Tapping the https one hands the phone a *file*: the events arrive once and
   * never update, which looks like success and goes stale in a week. Carried
   * as a link and never shown, like its sibling.
   */
  webcalUrl: string

  /** When a calendar client last collected it. Null until one does. */
  lastFetchedAt: string | null

  /**
   * The kinds of calendar that have collected it.
   *
   * **The only evidence she will ever have.** The link does not expire, so
   * nothing stops it but this list prompting a question — two entries when she
   * only ever added it in one place is something she can act on.
   */
  readBy: string[]
}

export async function fetchCalendarLinks(): Promise<CalendarLink[]> {
  const res = await fetch('/api/calendar/links')
  if (!res.ok) throw new Error(`Could not load your calendar links (${res.status})`)
  return res.json()
}

/**
 * Replaces every link this account holds, and returns the new ones.
 *
 * **All of them, never one.** The panel is a row per child, and a per-row
 * revoke is a puzzle at the moment somebody is worried about it.
 */
export async function replaceCalendarLinks(): Promise<CalendarLink[]> {
  const res = await fetch('/api/calendar/links/replace', { method: 'POST' })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not replace your links (${res.status})`)
  }

  return res.json()
}

/**
 * What a row says about who is reading it, or nothing at all.
 *
 * **The empty case is silence now** (D-577, amending D-533). That entry called
 * *"Nothing has read this yet"* the useful half, on the reasoning that a parent
 * who has just handed a URL to another app has no other way to tell whether it
 * worked. The owner read the shipped version and asked what it meant - `asked`:
 * *"we don't need that nothing has collected this yet, that doesn't make any
 * sense"* - and he is the best-informed reader this panel will ever have.
 *
 * **Under a dancer's name, before she has done anything, it reads as a fault
 * rather than a state.** It is the first thing on the row and it says something
 * is missing, at the moment when nothing is wrong and nothing is expected. The
 * evidence D-533 wanted survives in the other half, which only appears once
 * there is something true to say.
 *
 * Returns an empty string rather than a sentence; the row renders nothing.
 */
export function readerSummary(link: CalendarLink): string {
  if (link.readBy.length === 0) return ''

  // Kinds, never a count of addresses: Google fetches from rotating
  // infrastructure, so counting places would report ten for one subscription.
  return `Read by ${asList(link.readBy)}`
}

function asList(items: string[]): string {
  if (items.length === 1) return items[0]
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
